/**
 * Off-Page Brief index page (spec §5.7.2, Phase 1 shape).
 *
 * Phase 1 renders two live sub-tabs — Briefs + Accounts. Phase 2 (Outreach)
 * and Phase 3 (Impact) tabs are hidden entirely until the underlying
 * features ship; a stub tab with a "Coming soon" card looks broken and
 * doesn't exist anywhere else in the app. When those phases land the
 * TabsList below gets two extra triggers.
 *
 * Project selection lives in the header (same pattern as Briefs) so tenants
 * with multiple projects can switch context without leaving the page.
 */
import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Megaphone } from 'lucide-react';
import { Page, PageHeader, PageToolbar } from '@/components/ui/page';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EmptyState } from '@/components/ui/empty';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { useProjects } from '@/features/projects/use-projects';
import { useProjectFilterParam } from '@/lib/use-project-filter';
import { BriefsTable } from './BriefsTable';
import { AccountsPanel } from './AccountsPanel';

export function OffPageBriefsIndex(): JSX.Element {
  const projects = useProjects();
  const projectList = useMemo(
    () => projects.data?.items ?? [],
    [projects.data],
  );
  // Project selection lives in the URL (?project=) like every other list
  // page; localStorage seeds a default (falling back to the first project).
  const { projectId: selectedProject, setProjectId } = useProjectFilterParam({
    storageKey: 'seo:opb-project',
    projects: projectList,
    projectsLoading: projects.isLoading,
  });
  const projectId = selectedProject || projectList[0]?.id;
  const [query, setQuery] = useState('');

  const noProjects = !projects.isLoading && projectList.length === 0;

  return (
    <Page>
      <PageHeader
        title="Off-Page Brief"
        description="Ready-to-post variants for Reddit, LinkedIn, Medium, Quora, Dev.to, and X — built on top of a completed source brief."
        actions={
          !noProjects ? (
            <div className="min-w-[240px]">
              <Select
                value={projectId ?? ''}
                onValueChange={(v) => setProjectId(v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projectList.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null
        }
      />

      {noProjects ? (
        <EmptyState
          icon={Megaphone}
          title="No projects yet"
          description="Create a project first — Off-Page Briefs are scoped to a project."
          action={
            <Button asChild size="sm">
              <Link to="/projects">Go to Projects</Link>
            </Button>
          }
        />
      ) : (
        <Tabs defaultValue="briefs" className="space-y-4">
          <TabsList>
            <TabsTrigger value="briefs">Briefs</TabsTrigger>
            <TabsTrigger value="accounts">Accounts</TabsTrigger>
          </TabsList>

          <TabsContent value="briefs" className="space-y-4">
            <PageToolbar>
              <div className="relative w-full max-w-sm">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-8"
                  placeholder="Search by keyword or status…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            </PageToolbar>
            <BriefsTable projectId={projectId} query={query} />
          </TabsContent>

          <TabsContent value="accounts">
            <AccountsPanel />
          </TabsContent>
        </Tabs>
      )}
    </Page>
  );
}
